import { useState, useRef, useCallback, useEffect } from 'react';
import { getAgentWS } from '../utils/ws';

const SAMPLE_RATE = 16000;
const BUFFER_SIZE = 4096;
const TARGET_RMS = 0.15;
const MAX_GAIN = 3.0;
const MIN_GAIN = 0.5;
const AUDIO_LEVEL_SMOOTHING = 0.3;

function resampleAudio(inputData, inputSampleRate, outputSampleRate) {
    if (inputSampleRate === outputSampleRate) {
        return inputData;
    }
    const ratio = inputSampleRate / outputSampleRate;
    const outputLength = Math.floor(inputData.length / ratio);
    const output = new Float32Array(outputLength);
    for (let i = 0; i < outputLength; i++) {
        const srcIndex = i * ratio;
        const srcIndexFloor = Math.floor(srcIndex);
        const srcIndexCeil = Math.min(srcIndexFloor + 1, inputData.length - 1);
        const fraction = srcIndex - srcIndexFloor;
        output[i] = inputData[srcIndexFloor] * (1 - fraction) + inputData[srcIndexCeil] * fraction;
    }
    return output;
}

function float32ToPCM16(float32Array) {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < float32Array.length; i++) {
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
}

function calculateRMS(audioData) {
    let sum = 0;
    for (let i = 0; i < audioData.length; i++) {
        sum += audioData[i] * audioData[i];
    }
    return Math.sqrt(sum / audioData.length);
}

function normalizeAudio(audioData, targetRMS = TARGET_RMS) {
    const originalRMS = calculateRMS(audioData);
    if (originalRMS < 0.001) {
        return { data: audioData, gain: 1.0, originalRMS };
    }
    let gain = targetRMS / originalRMS;
    gain = Math.max(MIN_GAIN, Math.min(MAX_GAIN, gain));
    const normalized = new Float32Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
        normalized[i] = Math.max(-1, Math.min(1, audioData[i] * gain));
    }
    return { data: normalized, gain, originalRMS };
}

export function useStreamingSTT({
    language = 'auto',
    onPartialTranscript,
    onFinalTranscript,
    onAudioLevel,
    onError,
    onConnected,
    onDisconnected,
}) {
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [partialTranscript, setPartialTranscript] = useState('');
    const [audioLevel, setAudioLevel] = useState(0);

    const audioContextRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const processorRef = useRef(null);
    const isRecordingRef = useRef(false);
    const isActiveRef = useRef(false);
    const audioLevelRef = useRef(0);
    const partialTranscriptRef = useRef('');
    const finalTranscriptResolverRef = useRef(null);

    const callbacksRef = useRef({
        onPartialTranscript, onFinalTranscript, onAudioLevel, onError, onConnected, onDisconnected,
    });
    callbacksRef.current = {
        onPartialTranscript, onFinalTranscript, onAudioLevel, onError, onConnected, onDisconnected,
    };

    useEffect(() => {
        partialTranscriptRef.current = partialTranscript;
    }, [partialTranscript]);

    const cleanupMicrophone = useCallback(() => {
        isRecordingRef.current = false;
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        audioLevelRef.current = 0;
        setAudioLevel(0);
    }, []);

    const cleanup = useCallback(() => {
        isActiveRef.current = false;
        cleanupMicrophone();

        const ws = getAgentWS();
        ws.onVoiceEvent = null;

        setIsConnected(false);
        setIsConnecting(false);
        setPartialTranscript('');
        setAudioLevel(0);
    }, [cleanupMicrophone]);

    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    const handleVoiceEvent = useCallback((event) => {
        if (!isActiveRef.current) return;
        const cbs = callbacksRef.current;
        const { type, data } = event;

        switch (type) {
            case 'stt_ready':
                console.log('[StreamingSTT] STT ready');
                setIsConnected(true);
                cbs.onConnected?.();
                break;

            case 'stt_chunk': {
                const text = data?.text || '';
                partialTranscriptRef.current = text;
                setPartialTranscript(text);
                cbs.onPartialTranscript?.(text);
                break;
            }

            case 'stt_output': {
                const text = data?.text || '';
                const confidence = data?.confidence || 1.0;
                partialTranscriptRef.current = text;
                setPartialTranscript(text);
                cbs.onFinalTranscript?.(text, confidence);
                finalTranscriptResolverRef.current?.(text, confidence);
                break;
            }

            case 'no_speech_timeout': {
                const msg = data?.message || 'Tôi chưa nghe rõ mời bạn nói lại';
                cbs.onError?.(new Error(msg));
                break;
            }

            case 'silence_timeout': {
                const text = data?.text || '';
                partialTranscriptRef.current = text;
                setPartialTranscript(text);
                cbs.onFinalTranscript?.(text.trim(), 0.9);
                finalTranscriptResolverRef.current?.(text.trim(), 0.9);
                break;
            }

            default:
                break;
        }
    }, []);

    const waitForFinalTranscript = useCallback((timeoutMs = 10000) => {
        return new Promise((resolve) => {
            const timeout = setTimeout(() => {
                finalTranscriptResolverRef.current = null;
                resolve({ text: partialTranscriptRef.current, confidence: 0.5 });
            }, timeoutMs);

            finalTranscriptResolverRef.current = (text, confidence) => {
                clearTimeout(timeout);
                resolve({ text, confidence });
            };
        });
    }, []);

    const connect = useCallback(async () => {
        const ws = getAgentWS();
        if (!ws.isConnected) {
            onError?.(new Error('Kết nối bị gián đoạn, vui lòng thử lại'));
            throw new Error('Kết nối bị gián đoạn');
        }

        setIsConnecting(true);
        setPartialTranscript('');
        setAudioLevel(0);
        isActiveRef.current = true;

        await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Không thể kết nối nhận diện giọng nói, vui lòng thử lại'));
            }, 10000);

            ws.onVoiceEvent = (event) => {
                handleVoiceEvent(event);
                if (event.type === 'stt_ready') {
                    clearTimeout(timeout);
                    resolve();
                }
            };

            ws.sttStart({ language });
        });

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: SAMPLE_RATE,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });

            mediaStreamRef.current = stream;
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: SAMPLE_RATE,
            });

            const actualSampleRate = audioContextRef.current.sampleRate;
            const source = audioContextRef.current.createMediaStreamSource(stream);
            processorRef.current = audioContextRef.current.createScriptProcessor(BUFFER_SIZE, 1, 1);

            let chunkCount = 0;

            processorRef.current.onaudioprocess = (e) => {
                if (!isRecordingRef.current) return;

                const inputData = e.inputBuffer.getChannelData(0);

                const rms = calculateRMS(inputData);
                const normalizedLevel = Math.min(1, rms * 5);
                audioLevelRef.current = audioLevelRef.current * AUDIO_LEVEL_SMOOTHING +
                    normalizedLevel * (1 - AUDIO_LEVEL_SMOOTHING);
                setAudioLevel(audioLevelRef.current);
                callbacksRef.current.onAudioLevel?.(audioLevelRef.current);

                const { data: normalizedData } = normalizeAudio(inputData);

                let processedData = normalizedData;
                if (actualSampleRate !== SAMPLE_RATE) {
                    processedData = resampleAudio(normalizedData, actualSampleRate, SAMPLE_RATE);
                }

                const pcmData = float32ToPCM16(processedData);
                getAgentWS().sendBinary(pcmData);

                chunkCount++;
                if (chunkCount === 1) {
                    console.log(`[StreamingSTT] First chunk: ${pcmData.byteLength} bytes, rate: ${actualSampleRate}Hz`);
                }
            };

            source.connect(processorRef.current);
            processorRef.current.connect(audioContextRef.current.destination);

            isRecordingRef.current = true;
            setIsConnecting(false);
            console.log('[StreamingSTT] Recording started');
        } catch (error) {
            console.error('[StreamingSTT] Connect error:', error);
            setIsConnecting(false);
            cleanup();
            onError?.(error);
            throw error;
        }
    }, [language, handleVoiceEvent, cleanup, onError, isConnected]);

    const stopAndGetFinal = useCallback(async () => {
        if (!isActiveRef.current) {
            const text = partialTranscript;
            cleanup();
            return { text, confidence: 0.5 };
        }

        cleanupMicrophone();
        getAgentWS().sttStop();
        console.log('[StreamingSTT] Sent stt_stop, waiting for final...');

        try {
            const result = await waitForFinalTranscript(5000);
            cleanup();
            return result;
        } catch (error) {
            console.error('[StreamingSTT] Wait for final error:', error);
            const text = partialTranscript;
            cleanup();
            return { text, confidence: 0.5 };
        }
    }, [partialTranscript, waitForFinalTranscript, cleanup, cleanupMicrophone]);

    const disconnect = useCallback(() => {
        if (isActiveRef.current) {
            getAgentWS().sttStop();
        }
        cleanup();
    }, [cleanup]);

    const injectFileAudio = useCallback(async (audioBlob) => {
        if (!isActiveRef.current || !isRecordingRef.current) {
            console.log('[StreamingSTT] Cannot inject file - not active/recording');
            return;
        }

        try {
            const arrayBuffer = await audioBlob.arrayBuffer();
            const tempAudioContext = new (window.AudioContext || window.webkitAudioContext)();
            const audioBuffer = await tempAudioContext.decodeAudioData(arrayBuffer);
            const channelData = audioBuffer.getChannelData(0);
            const resampled = resampleAudio(channelData, audioBuffer.sampleRate, SAMPLE_RATE);
            tempAudioContext.close();

            const { data: normalizedData } = normalizeAudio(resampled);
            const totalChunks = Math.ceil(normalizedData.length / BUFFER_SIZE);

            for (let i = 0; i < totalChunks; i++) {
                if (!isRecordingRef.current || !isActiveRef.current) break;

                const start = i * BUFFER_SIZE;
                const end = Math.min(start + BUFFER_SIZE, normalizedData.length);
                const chunk = normalizedData.slice(start, end);
                const pcmData = float32ToPCM16(chunk);
                getAgentWS().sendBinary(pcmData);
            }

            console.log('[StreamingSTT] File injection complete');
        } catch (error) {
            console.error('[StreamingSTT] File injection error:', error);
        }
    }, []);

    return {
        isConnected,
        isConnecting,
        partialTranscript,
        audioLevel,
        connect,
        injectFileAudio,
        stopAndGetFinal,
        disconnect,
    };
}

export default useStreamingSTT;
